/**
 * TrailStore — a tiny ring buffer of trail-observability rows
 * (trails-and-afterimages spec §9). TrailSystem records ribbon
 * attach/release edges plus ~4Hz head/tail samples per live ribbon, and
 * ghost_spawn rows for dash afterimages; the ability-capture sidecar reads
 * the recent ones so the verifier's `expectTrail` checks have real data
 * (trails outlive their spawn moment — a one-shot lifecycle event can't
 * prove persistence).
 *
 * Singleton + ring buffer, mirroring ChainSegmentStore's shape. Pure data;
 * no Pixi/window. Cheap enough to always run (~4Hz per live ribbon).
 */
import type { TrailSample } from '../../shared/diagnostics/CaptureSidecarTypes';

const MAX_SAMPLES = 60;

class TrailStoreImpl {
  private buffer: TrailSample[] = [];

  record(sample: TrailSample): void {
    this.buffer.push(sample);
    if (this.buffer.length > MAX_SAMPLES) this.buffer.shift();
  }

  /** All buffered rows (newest last). */
  all(): readonly TrailSample[] {
    return this.buffer;
  }

  /** Rows at/after a timestamp floor — the cast-window slice. */
  since(tsFloor: number): TrailSample[] {
    return this.buffer.filter((s) => s.ts >= tsFloor);
  }

  clear(): void {
    this.buffer = [];
  }
}

export const TrailStore = new TrailStoreImpl();
